import React from "react";
import { Button, Fieldset, Switch, Tooltip } from "@flixbus/honeycomb-react";
import {
  Icon,
  IconEditSolid,
  IconPlus,
  IconUpdate,
} from "@flixbus/honeycomb-icons-react";
import type { CalculationLifecycleControlsProps } from "./CalculationLifecycleControls.types";
import {
  missingInputToggle,
  notComputableResults,
} from "../../calculationMessages.constants";
import styles from "./CalculationInfoBar.module.scss";

const CalculationLifecycleControls: React.FC<
  CalculationLifecycleControlsProps
> = ({
  status,
  hasIncompleteResults,
  activeSubcategoryHasCosts,
  incompleteReason,
  inFlight,
  onAddCost,
  onMarkComplete,
  onRecalculate,
  onReopen,
}) => {
  const incompleteDraft = status === "DRAFT" && hasIncompleteResults;
  const addCostLabel = activeSubcategoryHasCosts ? "Edit Cost" : "Add Cost";
  const incompleteTooltip =
    incompleteReason === "NOT_COMPUTABLE"
      ? notComputableResults
      : missingInputToggle;
  const toggle = (
    <Switch
      id="lifecycle-status-toggle"
      value="lifecycle-status-toggle"
      label={status === "COMPLETE" ? "Completed" : "Draft"}
      small
      checked={status === "COMPLETE"}
      disabled={inFlight !== null || incompleteDraft}
      onChange={() => {
        if (status === "DRAFT") onMarkComplete();
        else onReopen();
      }}
    />
  );

  return (
    <div className={styles.rightSection}>
      {status === "COMPLETE" ? (
        <Button
          appearance="primary"
          aria-label="Recalculate"
          loading={inFlight === "recalculate"}
          disabled={inFlight !== null}
          onClick={onRecalculate}
        >
          <Icon InlineIcon={IconUpdate} aria-hidden="true" />
          Recalculate
        </Button>
      ) : (
        <Tooltip
          id="add-cost-tooltip"
          content={addCostLabel}
          position="top"
          size="content-fit"
          openOnHover
        >
          <Button
            appearance="primary"
            display="square"
            aria-label={addCostLabel}
            onClick={onAddCost}
          >
            <Icon
              InlineIcon={activeSubcategoryHasCosts ? IconEditSolid : IconPlus}
              aria-hidden="true"
            />
          </Button>
        </Tooltip>
      )}
      <Fieldset extraClasses={styles.switchFieldset}>
        {incompleteDraft ? (
          <Tooltip
            id="lifecycle-toggle-tooltip"
            content={incompleteTooltip}
            position="left"
            openOnHover
          >
            <span tabIndex={0} className={styles.disabledToggleHoverTarget}>
              {toggle}
            </span>
          </Tooltip>
        ) : (
          toggle
        )}
      </Fieldset>
    </div>
  );
};

export default CalculationLifecycleControls;
